import ResourceNotFoundError from '@app/types/errors/ResourceNotFoundError';
import { Entry, EntryCollection, Locale } from 'contentful';
import { Environment, Entry as ManagementEntry } from 'contentful-management';
import pako from 'pako';
import IPresentationMaster from '../../types/presentations/master/IPresentationMaster';
import IRawPresentationMaster from '../../types/presentations/master/IRawPresentationMaster';
import AbstractPresentationMasterService from './AbstractPresentationMasterService';

export default class PresentationMasterSnapshotService extends AbstractPresentationMasterService {
	// number of chunks compressed presentation data is split across
	private static readonly NUM_DATA_CHUNKS: number = 4;

	private static readonly CONTENT_TYPE_MASTER_SNAPSHOT = 'presentationMasterSnapshot';

	private static readonly CONTENT_TYPE_MASTER_SNAPSHOT_CHUNK = 'presentationMasterSnapshotChunk';

	/**
	 * Fetch a presentation master snapshot from Contentful by its master id and checksum (not: snapshot entry id)
	 *
	 * @param id
	 * @param checksum
	 */
	public async getByMasterIdAndChecksum(id: string, checksum: string): Promise<Record<string, IPresentationMaster>> {
		const entryCollection: EntryCollection<any> = await this.deliveryClient.getEntries({
			links_to_entry: id, // eslint-disable-line
			content_type: PresentationMasterSnapshotService.CONTENT_TYPE_MASTER_SNAPSHOT, // eslint-disable-line
			// eslint-disable-next-line @typescript-eslint/naming-convention
			'fields.checksum': checksum,
			include: 1,
			limit: 1,
			order: '-sys.createdAt' as any
		});
		const entry: Entry<any> | undefined = entryCollection.items.shift();

		if (!entry) {
			throw new ResourceNotFoundError(
				`Presentation master snapshot entry with id '${id}' could not be retrieved as it does not exist.`
			);
		}

		return this.parse(entry);
	}

	public async create(
		rawPresentationMaster: IRawPresentationMaster<'WITH_ALL_LOCALES'>
	): Promise<Record<string, IPresentationMaster>> {
		const managementEnvironment: Environment = await this.getManagementEnvironment();

		// strip unneeded data from the presentation master in order to reduce JSON payload in size
		// when creating the snapshot in Contentful
		const rawPresentationMasterSnapsShotData: Entry<any> = rawPresentationMaster.entry;

		// limit changelog to the 10 newest entries
		(rawPresentationMasterSnapsShotData.fields.changelog as any).de.splice(10);

		// compress entry into a single string
		const deflatedData: string = btoa(
			pako.deflate(JSON.stringify(rawPresentationMasterSnapsShotData), { to: 'string', level: 9 })
		);

		// split the string with the compressed master snapshot into chunks of about equal length,
		// so we can create a dedicated entry for each chunk
		const chunkSize = Math.ceil(deflatedData.length / 4);
		const dataChunks: string[] = [];
		let chunkIndex: number = 0;

		for (chunkIndex; chunkIndex < PresentationMasterSnapshotService.NUM_DATA_CHUNKS; chunkIndex++) {
			dataChunks.push(deflatedData.substr(chunkIndex * chunkSize, chunkSize));
		}

		let chunkEntryLinks: Record<string, any>[] = [];
		const createdEntryIds: string[] = [];

		try {
			// save all chunk entries individually first and build a list with their link objects
			// so we can save those to the snapshot entry
			chunkEntryLinks = await Promise.all(
				dataChunks.map(async (chunk: string): Promise<Record<string, any>> => {
					const entry: ManagementEntry = await managementEnvironment.createEntry(
						PresentationMasterSnapshotService.CONTENT_TYPE_MASTER_SNAPSHOT_CHUNK,
						{
							fields: {
								data: {
									de: {
										deflated: chunk
									}
								}
							}
						}
					);

					// memorize this entry has been created in case we need to roll back
					createdEntryIds.push(entry.sys.id);

					await entry.publish();

					// a link object that can be used to to reference this chunk entry in the snapshot entry
					return {
						sys: {
							linkType: 'Entry',
							id: entry.sys.id
						}
					};
				})
			);

			// save the snapshot itself and link the created chunk entries
			const entry: ManagementEntry = await managementEnvironment.createEntry(
				PresentationMasterSnapshotService.CONTENT_TYPE_MASTER_SNAPSHOT,
				{
					fields: {
						internalTitle: {
							de: `${rawPresentationMaster.id} - ${rawPresentationMaster.checksum}`
						},
						checksum: {
							de: rawPresentationMaster.checksum
						},
						presentationMaster: {
							de: {
								sys: {
									linkType: 'Entry',
									id: rawPresentationMaster.id
								}
							}
						},
						dataChunks: {
							de: chunkEntryLinks
						}
					}
				}
			);

			// memorize this entry has been created in case we need to roll back
			createdEntryIds.push(entry.sys.id);

			await entry.publish();

			return this.getByMasterIdAndChecksum(rawPresentationMaster.id, rawPresentationMaster.checksum);
		} catch (error) {
			if (error instanceof Error && error.message) {
				console.error('Could not create presentation master snapshot because of the following error:', error.message);
			}

			// unpublish and delete all created entries in reverse order of creation for a clean rollback fo the entire operation
			await Promise.all(
				createdEntryIds.reverse().map(async (entryId: string) => {
					try {
						let entry: ManagementEntry = await managementEnvironment.getEntry(entryId);
						try {
							entry = await entry.unpublish();
						} finally {
							// ignore potential unpublish errors and delete the entry
							await entry.delete();
						}
					} catch (rollbackError) {
						console.error(
							'Additionally, the following error occurred during deletion of the snapshot entry or one of its data chunks:',
							rollbackError
						);
					}
				})
			);

			throw error;
		}
	}

	private async parse(entry: Entry<any>): Promise<Record<string, IPresentationMaster>> {
		let deflatedString: string;

		//  use new data format with chunks where available
		if (entry.fields.dataChunks && (entry.fields.dataChunks as any as ManagementEntry[]).length) {
			// reduce linked data entries to one single deflated string value
			deflatedString = (entry.fields.dataChunks as any as ManagementEntry[]).reduce(
				(currentDeflatedString: string, chunkEntry): string =>
					`${currentDeflatedString}${chunkEntry.fields.data.deflated}`,
				''
			);
		}
		// fallback for old data format with just one single data field
		else {
			deflatedString = (entry.fields.data as any).deflated;
		}

		const masterEntry: Entry<any> = JSON.parse(pako.inflate(atob(deflatedString), { to: 'string' }));
		const mastersByLocale: Record<string, IPresentationMaster> = {};

		this.locales.forEach((locale: Locale): void => {
			mastersByLocale[locale.code] = {
				...this.mapPresentationMasterEntry(
					{
						id: masterEntry.sys.id,
						entry: this.mapToSingleLocaleObject(masterEntry, locale),
						checksum: entry.fields.checksum as string
					},
					locale.code
				),
				snapshotId: entry.sys.id,
				dateCreated: Date.parse(entry.sys.createdAt)
			};
		});

		return mastersByLocale;
	}
}
